import { ReactNode } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';

import Icon from '@/common/components/Icon/Icon';
import { IconName } from '@/common/components/Icon/iconName';

import styles from './MetricScaleContainer.module.css';

export const SIZE_XSMALL = 'xs';
export const SIZE_SMALL = 's';
export const SIZE_MEDIUM = 'm';
export const SIZE_LARGE = 'l';

export type MetricScaleSize = typeof SIZE_XSMALL | typeof SIZE_SMALL | typeof SIZE_MEDIUM | typeof SIZE_LARGE;

interface MetricScaleContainerProps {
  scale: ReactNode;
  label?: Nullable<ReactNode>;
  icon?: Nullable<IconName>;
  abs?: Nullable<string>;
  size?: MetricScaleSize;
  isMetricCalculated?: boolean;
  className?: string;
  inCard?: boolean;
}

function MetricScaleContainer(props: MetricScaleContainerProps) {
  const {
    scale,
    label = null,
    icon = null,
    abs = '—',
    size = SIZE_MEDIUM,
    isMetricCalculated = true,
    className,
    inCard = false,
  } = props;

  if (size === SIZE_XSMALL) {
    return (
      <div
        className={cls(styles.metricScaleContainerXSmall, { [styles.metricScaleContainerInCard]: inCard }, className)}
      >
        <div className={styles.metricScaleContainerXSmallABS} aria-label="Absolute Value">
          {abs}
        </div>

        <div className={styles.metricScaleContainerXSmallScale}>{scale}</div>
      </div>
    );
  }

  if (size === SIZE_SMALL) {
    return (
      <div
        className={cls(
          styles.metricScaleContainerSmall,
          {
            [styles.metricScaleContainerActive]: isMetricCalculated,
            [styles.metricScaleContainerInactive]: !isMetricCalculated,
            [styles.metricScaleContainerInCard]: inCard,
          },
          className,
        )}
      >
        <div className={styles.metricScaleContainerSmallTitle}>
          <div className={styles.metricScaleContainerSmallIcon}>{icon && <Icon name={icon} />}</div>

          <span className={styles.metricScaleContainerLabel}>{label}</span>

          <span className="ml-auto" aria-label="Absolute Value">
            {abs}
          </span>
        </div>

        {scale}
      </div>
    );
  }

  if (size === SIZE_MEDIUM) {
    return (
      <div
        className={cls(
          styles.metricScaleContainerMedium,
          {
            [styles.metricScaleContainerActive]: isMetricCalculated,
            [styles.metricScaleContainerInactive]: !isMetricCalculated,
            [styles.metricScaleContainerInCard]: inCard,
          },
          className,
        )}
      >
        <div className={styles.metricScaleContainerMediumWrapper}>
          <div className={styles.metricScaleContainerMediumTitle}>
            <div className={styles.metricScaleContainerMediumIcon}>{icon && <Icon name={icon} />}</div>

            <span className={styles.metricScaleContainerLabel}>{label}</span>

            <span className="ml-auto" aria-label="Absolute Value">
              {abs}
            </span>
          </div>

          <div className={styles.metricScaleContainerMediumScale}>{scale}</div>
        </div>
      </div>
    );
  }

  if (size === SIZE_LARGE) {
    return (
      <div
        className={cls(
          styles.metricScaleContainerLarge,
          {
            [styles.metricScaleContainerActive]: isMetricCalculated,
            [styles.metricScaleContainerInactive]: !isMetricCalculated,
            [styles.metricScaleContainerInCard]: inCard,
          },
          className,
        )}
      >
        <div className={styles.metricScaleContainerLargeIcon}>{icon && <Icon name={icon} />}</div>

        <div className={styles.metricScaleContainerLargeTitle}>{label}</div>

        <div className={styles.metricScaleContainerLargeABS} aria-label="Absolute Value">
          {abs}
        </div>

        <div className={styles.metricScaleContainerLargeScale}>{scale}</div>
      </div>
    );
  }

  return null;
}

export default observer(MetricScaleContainer);
